import React, { useState, useEffect } from 'react';
import { useEntryState } from '@/lib/entryState';
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import PersonPick from "@/components/PersonPick";
import { AddCustomer } from "@/estimating/components/BoardCells";
import HeaderFilterMenu from "@/estimating/components/HeaderFilterMenu";
import { shortName } from "@/lib/people";
import { useAuth } from "@/lib/AuthContext";
import { ChevronsUpDown, ArrowUp, ArrowDown } from "lucide-react";
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Building2, MapPin, Users, User, Tag, Calendar, Edit, Trash2, FolderOpen, MoreHorizontal, Filter, UserPlus, Pin, PinOff, Clock, Upload, Loader2, AlertTriangle, HardDrive } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

const SortTh = ({ label, k, sort, setSort, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-2 text-left ${className}`}>
    <button type="button" data-testid={`sb-sort-${k}`}
            onClick={() => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }))}
            className={`inline-flex items-center gap-1 hover:text-foreground ${sort.key === k ? "text-foreground" : ""}`}>
      {label}
      {sort.key !== k ? <ChevronsUpDown className="h-3 w-3 opacity-40" /> : sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
    </button>
  </th>
);

// GC / Architect arrive with the won job; only an empty cell offers the Directory picker.
const PartyCell = ({ value, type, testId, onSave }) => (
  value ? <span className="text-sm text-slate-600" data-testid={testId}>{value}</span> : (
    <AddCustomer job={{ id: "", project_name: "" }} companyType={type} testid={testId} already={[]}
                 onPick={(name) => onSave(name)}
                 onNew={() => window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "settings", sub: "directory" } }))} />
  )
);

export default function Projects({ onProjectSelect, onBack }) {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useEntryState('q', '');
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    job_number: '',
    name: '',
    location: '',
    gc: '',
    architect: '',
    pm: '',
    estimator: '',
    tags: ''
  });
  
  // Track when "Add new" is selected for PM/Estimator
  const [showNewPmInput, setShowNewPmInput] = useState(false);
  const [showNewEstimatorInput, setShowNewEstimatorInput] = useState(false);
  
  // Simple "Add New Name" dialog state
  const [addNameDialog, setAddNameDialog] = useState({ open: false, type: '', projectId: '' });
  const [newNameInput, setNewNameInput] = useState('');
  
  // Team members for dropdowns
  const [teamMembers, setTeamMembers] = useState({ pms: [], estimators: [], project_engineers: [] });
  
  // Delete team member confirmation
  const [deleteTeamMemberOpen, setDeleteTeamMemberOpen] = useState(false);
  const [teamMemberToDelete, setTeamMemberToDelete] = useState({ type: '', name: '' });

  // Filter by team member
  const [filterByPerson, setFilterByPerson] = useState({ pm: '', project_engineer: '', estimator: '' });
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  const { user } = useAuth();
  const [groupBy, setGroupBy] = useEntryState('groupBy', '');
  const pickGroup = (k) => setGroupBy((g) => (g === k ? '' : k));
  const [projectProgress, setProjectProgress] = useState({});
  const personOpts = (f) => [...new Set(projects.map((p) => p[f]).filter(Boolean))].sort()
    .map((v) => ({ value: v, label: shortName(v) })).concat({ value: '~', label: 'Unassigned' });

  // Retention: projects nearing permanent deletion
  const [retentionPending, setRetentionPending] = useState([]);
  const [retentionDialogOpen, setRetentionDialogOpen] = useState(false);
  const [keepChecked, setKeepChecked] = useState({});
  const [keepSaving, setKeepSaving] = useState(false);
  const [storageUsage, setStorageUsage] = useState(null);

  const formatBytes = (b) => {
    if (b == null) return '—';
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
    if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(0)} MB`;
    return `${Math.max(1, Math.round(b / 1024))} KB`;
  };

  const fetchRetentionPending = async () => {
    try {
      const res = await axios.get(`${API}/retention/pending`);
      setRetentionPending(res.data.pending || []);
    } catch (e) { /* non-blocking */ }
  };
  useEffect(() => {
    fetchRetentionPending();
    axios.get(`${API}/admin/storage-usage`).then(res => setStorageUsage(res.data)).catch(() => {});
  }, []);

  // Command palette quick actions
  useEffect(() => {
    const openCreate = () => setCreateModalOpen(true);
    const openFrs = () => frsInputRef.current?.click();
    window.addEventListener('sb:open-create-project', openCreate);
    window.addEventListener('sb:open-frs-upload', openFrs);
    return () => {
      window.removeEventListener('sb:open-create-project', openCreate);
      window.removeEventListener('sb:open-frs-upload', openFrs);
    };
  }, []);

  const handleKeepSelected = async () => {
    const ids = Object.keys(keepChecked).filter(id => keepChecked[id]);
    if (!ids.length) { toast.error('Select at least one project to keep'); return; }
    setKeepSaving(true);
    try {
      await axios.post(`${API}/retention/keep`, { project_ids: ids });
      toast.success(`Kept ${ids.length} project${ids.length > 1 ? 's' : ''} — 90-day clock reset`);
      setKeepChecked({});
      await fetchRetentionPending();
    } catch (e) {
      toast.error('Failed to update retention');
    } finally {
      setKeepSaving(false);
    }
  };

  // Load projects on mount
  useEffect(() => {
    loadProjects();
    loadTeamMembers();
  }, []);

  // Filter projects when filters change
  useEffect(() => {
    filterProjects();
  }, [projects, searchQuery, statusFilter, filterByPerson, sort, projectProgress, groupBy]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`, { params: { page_size: 1000 } });
      const data = response.data;
      setProjects(Array.isArray(data) ? data : (data.items || []));
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/projects/team-members`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const handleDeleteTeamMember = async () => {
    const { type, name } = teamMemberToDelete;
    if (!type || !name) return;
    
    try {
      await axios.delete(`${API}/projects/team-members/${type}/${encodeURIComponent(name)}`);
      const typeLabel = type === 'pm' ? 'PM' : type === 'project_engineer' ? 'Project Engineer' : 'Estimator';
      toast.success(`${typeLabel} "${name}" removed from all projects`);
      setDeleteTeamMemberOpen(false);
      setTeamMemberToDelete({ type: '', name: '' });
      loadTeamMembers();
      loadProjects();
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const openDeleteTeamMemberDialog = (type, name) => {
    setTeamMemberToDelete({ type, name });
    setDeleteTeamMemberOpen(true);
  };

  // Inline update PM, Estimator, or Project Engineer
  const handleInlineTeamMemberUpdate = async (projectId, field, value) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { [field]: value || null });
      const fieldLabel = { pm: 'PM', project_engineer: 'PE', estimator: 'Estimator', gc: 'GC', architect: 'Architect' }[field] || 'Job';
      toast.success(`${fieldLabel} updated`);
      loadProjects();
      loadTeamMembers();
    } catch (error) {
      console.error('Error updating team member:', error);
      toast.error('Failed to update');
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        [p.gc, p.architect, p.pm, p.project_engineer, p.estimator].some((v) => (v || '').toLowerCase().includes(query)) ||
        p.name.toLowerCase().includes(query) ||
        (p.job_number || '').toLowerCase().includes(query) ||
        (p.location && p.location.toLowerCase().includes(query)) ||
        (p.tags && p.tags.toLowerCase().includes(query))
      );
    }

    // Filter by team member
    if (groupBy === 'mine') {
      const me = [user?.name, user?.email].filter(Boolean).map((v) => v.toLowerCase());
      filtered = filtered.filter((p) => [p.pm, p.project_engineer, p.estimator].some((v) => me.includes((v || '').toLowerCase())));
    }
    ['pm', 'project_engineer'].forEach((f) => {
      if (filterByPerson[f]) filtered = filtered.filter((p) => filterByPerson[f].split('|').includes(p[f] || '~'));
    });

    const pick = {
      job: (p) => (p.job_number ? String(p.job_number).padStart(10, '0') : '~'),
      name: (p) => (p.name || '').toLowerCase(), gc: (p) => (p.gc || '~').toLowerCase(),
      architect: (p) => (p.architect || '~').toLowerCase(), pm: (p) => (p.pm || '~').toLowerCase(),
      project_engineer: (p) => (p.project_engineer || '~').toLowerCase(), estimator: (p) => (p.estimator || '~').toLowerCase(),
      created: (p) => new Date(p.created_at).getTime(), progress: (p) => getProjectProgress(p).done / (getProjectProgress(p).total || 1),
    }[sort.key];
    filtered.sort((a, b) => { const x = pick(a), y = pick(b); const n = x > y ? 1 : x < y ? -1 : 0; return sort.dir === 'asc' ? n : -n; });

    setFilteredProjects(filtered);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/projects`, formData);
      const newProject = response.data;
      
      toast.success(`Project "${newProject.name}" created!`, {
        duration: 3000
      });
      
      setCreateModalOpen(false);
      resetForm();
      // Navigate directly to the new project's setup page
      onProjectSelect(newProject);
      loadTeamMembers();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/projects/${currentProject.id}`, formData);
      toast.success('Project updated successfully!');
      setEditModalOpen(false);
      resetForm();
      setCurrentProject(null);
      loadProjects();
      loadTeamMembers(); // Refresh team members for dropdowns
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  const openDeleteDialog = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const [uploadingFrs, setUploadingFrs] = useState(false);
  const frsInputRef = React.useRef(null);
  
  const handleFrsAutoProject = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFrs(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/frs-auto-project`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = response.data;
      toast.success(`Project "${result.project.name}" created with ${result.total_scopes} scopes!`, { duration: 5000 });
      
      // Navigate to the new project
      onProjectSelect(result.project);
    } catch (error) {
      console.error('FRS auto-project failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project from FRS');
    } finally {
      setUploadingFrs(false);
      if (frsInputRef.current) frsInputRef.current.value = '';
    }
  };

  const handleDeleteProject = async (e) => {
    e.preventDefault();
    if (!projectToDelete) return;

    try {
      await axios.delete(`${API}/projects/${projectToDelete.id}`);
      toast.success('Project archived successfully!');
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive project');
      setDeleteDialogOpen(false);
    }
  };

  const openEditModal = (project) => {
    setCurrentProject(project);
    setFormData({
      job_number: project.job_number,
      name: project.name,
      location: project.location || '',
      gc: project.gc || '',
      architect: project.architect || '',
      pm: project.pm || '',
      estimator: project.estimator || '',
      tags: project.tags || ''
    });
    setEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      job_number: '',
      name: '',
      location: '',
      gc: '',
      architect: '',
      pm: '',
      estimator: '',
      tags: ''
    });
    setShowNewPmInput(false);
    setShowNewEstimatorInput(false);
  };

  // Handle adding a new PM or Estimator name via simple dialog
  const handleAddNewName = async () => {
    if (!newNameInput.trim()) {
      toast.error('Please enter a name');
      return;
    }

    const { type, projectId } = addNameDialog;
    
    try {
      // Update the project with the new name
      await axios.put(`${API}/projects/${projectId}`, {
        [type]: newNameInput.trim()
      });
      
      const typeLabel = type === 'pm' ? 'PM' : type === 'project_engineer' ? 'Project Engineer' : 'Estimator';
      toast.success(`${typeLabel} "${newNameInput.trim()}" added!`);
      
      // Close dialog and refresh data
      setAddNameDialog({ open: false, type: '', projectId: '' });
      setNewNameInput('');
      loadProjects();
      loadTeamMembers();
    } catch (error) {
      console.error('Error adding name:', error);
      toast.error('Failed to add name');
    }
  };

  const handleTogglePin = async (projectId) => {
    try {
      const res = await axios.put(`${API}/projects/${projectId}/pin`);
      const pinned = res.data.pinned;
      toast.success(pinned ? 'Project pinned' : 'Project unpinned');
      loadProjects();
    } catch (error) {
      console.error('Error toggling pin:', error);
      toast.error('Failed to update pin');
    }
  };

  const handleProjectOpen = async (project) => {
    try {
      await axios.put(`${API}/projects/${project.id}/open`);
    } catch (error) {
      console.error('Error tracking open:', error);
    }
    onProjectSelect(project);
  };

  // Derive pinned and recently opened lists from projects
  const pinnedProjects = projects.filter(p => p.pinned && p.status === 'active');
  const recentlyOpened = projects
    .filter(p => p.last_opened_at && p.status === 'active' && !p.pinned)
    .sort((a, b) => new Date(b.last_opened_at) - new Date(a.last_opened_at))
    .slice(0, 4);
  const showQuickAccess = pinnedProjects.length > 0 || recentlyOpened.length > 0;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Per-project submittal progress, in one call (the backend classifies each submittal's stage).

  useEffect(() => {
    if (projects.length === 0) return;
    axios.get(`${API}/projects/progress`)
      .then((res) => setProjectProgress(res.data?.progress || {}))
      .catch((e) => console.error('Error loading project progress:', e));
  }, [projects]);

  const EMPTY_PROGRESS = { total: 0, setup: 0, ready: 0, docs_needed: 0, built: 0, submitted: 0,
                           missing_docs: 0, blocked: [] };

  const groups = (() => {
    if (!['pm', 'project_engineer'].includes(groupBy)) return [{ label: '', rows: filteredProjects }];
    const m = new Map();
    filteredProjects.forEach((p) => { const k = p[groupBy] ? shortName(p[groupBy]) : 'Unassigned'; m.set(k, [...(m.get(k) || []), p]); });
    return [...m.keys()].sort((a, b) => (a === 'Unassigned') - (b === 'Unassigned') || a.localeCompare(b)).map((k) => ({ label: k, rows: m.get(k) }));
  })();
  const getProjectProgress = (project) => {
    const p = projectProgress[project.id] || EMPTY_PROGRESS;
    return { ...p, done: p.built + p.submitted };
  };

  return (
    <div className="min-h-screen bg-background" data-testid="projects-page">
      {/* Header Section */}
      <div className="bg-background">
        <div className="px-2 pt-1 pb-3">
          <div className="flex flex-wrap items-center gap-2">
              <SearchBar value={searchQuery} onChange={setSearchQuery}
                         placeholder="Search..." testId="project-search-input" />
              <Segmented value={statusFilter} onChange={setStatusFilter}
                         options={[{ key: "active", label: "Active", testId: "submittals-view-active" },
                                   { key: "archived", label: "Archived", testId: "submittals-view-archived" }]}>
                <span className="mx-0.5 h-4 w-px bg-slate-300" />
                {[["mine", "My Subs"], ["pm", "PM"], ["project_engineer", "PE"]].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => pickGroup(k)} data-testid={`sb-group-pill-${k}`}
                          className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                            groupBy === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                    {label}
                  </button>))}
              </Segmented>
              <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" title="New Submittal"
                          data-testid="create-project-btn">
                    <Plus className="mr-0.5 h-3.5 w-3.5" /> Submittal
                  </Button>
                </DialogTrigger>
                {/* FRS Auto-Project Upload Button */}
                <input
                  ref={frsInputRef}
                  type="file"
                  accept=".xlsx,.xlsm,.xls"
                  className="hidden"
                  onChange={handleFrsAutoProject}
                  data-testid="frs-auto-project-input"
                />
                <Button
                  size="sm" variant="data" title="Upload FRS"
                  onClick={() => frsInputRef.current?.click()}
                  disabled={uploadingFrs}
                  className="h-7 px-2 text-xs"
                  data-testid="frs-auto-project-btn"
                >
                  {uploadingFrs ? (
                    <><Loader2 className="mr-0.5 h-3.5 w-3.5 animate-spin" /> FRS</>
                  ) : (
                    <><Upload className="mr-0.5 h-3.5 w-3.5" /> FRS</>
                  )}
                </Button>
                <DialogContent className="sm:max-w-[500px] bg-card">
                  <DialogHeader>
                    <DialogTitle className="text-xl text-foreground">Create New Project</DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                      Add a new submittal project to your workspace.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="job_number" className="text-muted-foreground">Job Number *</Label>
                        <Input
                          id="job_number"
                          required
                          value={formData.job_number}
                          onChange={(e) => setFormData({...formData, job_number: e.target.value})}
                          placeholder=""
                          className="text-foreground"
                          data-testid="job-number-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-muted-foreground">Project Name *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder=""
                          className="text-foreground"
                          data-testid="project-name-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-muted-foreground">Location</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder=""
                        className="text-foreground"
                        data-testid="location-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="gc" className="text-muted-foreground">General Contractor</Label>
                        <Input
                          id="gc"
                          value={formData.gc}
                          onChange={(e) => setFormData({...formData, gc: e.target.value})}
                          placeholder=""
                          className="text-foreground"
                          data-testid="gc-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="architect" className="text-muted-foreground">Architect</Label>
                        <Input
                          id="architect"
                          value={formData.architect}
                          onChange={(e) => setFormData({...formData, architect: e.target.value})}
                          placeholder=""
                          className="text-foreground"
                          data-testid="architect-input"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="pm" className="text-muted-foreground">Project Manager</Label>
                          {teamMembers.pms.length > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                if (formData.pm) {
                                  openDeleteTeamMemberDialog('pm', formData.pm);
                                }
                              }}
                              disabled={!formData.pm}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                        {!showNewPmInput ? (
                          <Select
                            value={formData.pm}
                            onValueChange={(value) => {
                              if (value === '__new__') {
                                setShowNewPmInput(true);
                                setFormData({...formData, pm: ''});
                              } else {
                                setFormData({...formData, pm: value});
                              }
                            }}
                          >
                            <SelectTrigger className="text-foreground" data-testid="pm-select">
                              <SelectValue placeholder="Select PM (optional)" />
                            </SelectTrigger>
                            <SelectContent className="bg-card">
                              {teamMembers.pms.map((pm) => (
                                <SelectItem key={pm} value={pm}>{pm}</SelectItem>
                              ))}
                              <SelectItem value="__new__">+ Add new PM</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Enter PM name"
                              value={formData.pm}
                              onChange={(e) => setFormData({...formData, pm: e.target.value})}
                              className="text-foreground flex-1"
                              data-testid="pm-new-input"
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setShowNewPmInput(false);
                                setFormData({...formData, pm: ''});
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="estimator" className="text-muted-foreground">Estimator</Label>
                          {teamMembers.estimators.length > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                if (formData.estimator) {
                                  openDeleteTeamMemberDialog('estimator', formData.estimator);
                                }
                              }}
                              disabled={!formData.estimator}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                        {!showNewEstimatorInput ? (
                          <Select
                            value={formData.estimator}
                            onValueChange={(value) => {
                              if (value === '__new__') {
                                setShowNewEstimatorInput(true);
                                setFormData({...formData, estimator: ''});
                              } else {
                                setFormData({...formData, estimator: value});
                              }
                            }}
                          >
                            <SelectTrigger className="text-foreground" data-testid="estimator-select">
                              <SelectValue placeholder="Select Estimator (optional)" />
                            </SelectTrigger>
                            <SelectContent className="bg-card">
                              {teamMembers.estimators.map((est) => (
                                <SelectItem key={est} value={est}>{est}</SelectItem>
                              ))}
                              <SelectItem value="__new__">+ Add new Estimator</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Enter Estimator name"
                              value={formData.estimator}
                              onChange={(e) => setFormData({...formData, estimator: e.target.value})}
                              className="text-foreground flex-1"
                              data-testid="estimator-new-input"
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setShowNewEstimatorInput(false);
                                setFormData({...formData, estimator: ''});
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <DialogFooter className="gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setCreateModalOpen(false);
                          resetForm();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" data-testid="submit-create-project-btn">
                        Create Project
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              {storageUsage && (
                <div className="ml-auto inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1"
                     data-testid="storage-meter"
                     title={`Stored files: ${formatBytes(storageUsage.gridfs_bytes)} (${storageUsage.gridfs_files} files) · Database: ${formatBytes(storageUsage.db_bytes)} · Local cache: ${formatBytes(storageUsage.local_cache_bytes)}`}>
                  <HardDrive className="h-3.5 w-3.5 text-primary" />
                  <span className="whitespace-nowrap text-xs font-medium text-foreground">{formatBytes(storageUsage.db_bytes + storageUsage.local_cache_bytes)} used</span>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">· auto-cleaned after {storageUsage.retention_days} days</span>
                </div>
              )}
          </div>
        </div>
      </div>


      {/* Retention warning banner */}
      {retentionPending.length > 0 && (
        <div className="px-2 pt-4" data-testid="retention-banner">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-800 dark:text-amber-200 truncate">
                {retentionPending.length} project{retentionPending.length !== 1 ? 's' : ''} scheduled for permanent deletion — review and keep the ones your team still needs
              </span>
            </div>
            <Button size="sm" variant="outline" className="shrink-0 border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800" onClick={() => setRetentionDialogOpen(true)} data-testid="retention-review-btn">
              Review
            </Button>
          </div>
        </div>
      )}

      {/* Retention review dialog */}
      <Dialog open={retentionDialogOpen} onOpenChange={setRetentionDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="retention-dialog">
          <DialogHeader>
            <DialogTitle>Projects Scheduled for Deletion</DialogTitle>
            <DialogDescription>
              These projects will be permanently deleted when their 90-day retention period expires.
              Check the ones your team wants to keep — keeping a project resets its 90 days from today.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2 py-1">
            {retentionPending.map(p => (
              <label key={p.id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-muted/50" data-testid={`retention-item-${p.id}`}>
                <Checkbox
                  checked={!!keepChecked[p.id]}
                  onCheckedChange={(v) => setKeepChecked(prev => ({ ...prev, [p.id]: !!v }))}
                  data-testid={`retention-keep-checkbox-${p.id}`}
                />
                <div className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground truncate">{p.name}</span>
                  <span className="block text-xs text-muted-foreground font-mono">#{p.job_number}</span>
                </div>
                <span className={`text-xs shrink-0 ${p.days_left <= 3 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground'}`}>
                  {p.days_left === 0 ? 'deleting soon' : `${p.days_left} day${p.days_left !== 1 ? 's' : ''} left`} · {p.delete_on}
                </span>
              </label>
            ))}
            {retentionPending.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No projects pending deletion.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetentionDialogOpen(false)} data-testid="retention-close-btn">Close</Button>
            <Button onClick={handleKeepSelected} disabled={keepSaving} data-testid="retention-keep-btn">
              {keepSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Keep Selected (reset 90 days)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Access Section - Pinned & Recently Opened */}
      {!loading && showQuickAccess && statusFilter === 'active' && !searchQuery && (
        <div className="px-2 pt-1">
          <div className="flex gap-6">
            {/* Pinned Projects */}
            {pinnedProjects.length > 0 && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3">
                  <Pin className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pinned</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {pinnedProjects.map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleProjectOpen(p)}
                      className="group relative text-left bg-card border border-border rounded-lg p-3 hover:border-primary/50 hover:shadow-sm transition-all"
                      data-testid={`pinned-project-${p.id}`}
                    >
                      <button
                        onClick={(e) => { e.stopPropagation(); handleTogglePin(p.id); }}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-amber-500"
                        data-testid={`unpin-${p.id}-btn`}
                      >
                        <PinOff className="h-3.5 w-3.5" />
                      </button>
                      <span className="block text-sm font-medium text-foreground truncate pr-5">{p.name}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5 font-mono">#{p.job_number}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recently Opened */}
            {recentlyOpened.length > 0 && (
              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {recentlyOpened.map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleProjectOpen(p)}
                      className="group text-left bg-card border border-border rounded-lg p-3 hover:border-primary/50 hover:shadow-sm transition-all"
                      data-testid={`recent-project-${p.id}`}
                    >
                      <span className="block text-sm font-medium text-foreground truncate">{p.name}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5 font-mono">#{p.job_number}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="px-2 py-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card className="bg-card border border-border">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderOpen className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <p className="text-lg font-medium text-foreground mb-2">No projects found</p>
              <p className="text-sm text-muted-foreground mb-6">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Create your first project to get started'
                }
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button onClick={() => setCreateModalOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Project
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm" data-testid="sb-projects-table">
              <thead className="bg-muted/40 text-[11px] font-semibold text-muted-foreground">
                <tr>
                  <SortTh label="Job #" k="job" sort={sort} setSort={setSort} />
                  <SortTh label="Name" k="name" sort={sort} setSort={setSort} />
                  <SortTh label="GC" k="gc" sort={sort} setSort={setSort} />
                  <SortTh label="Architect" k="architect" sort={sort} setSort={setSort} />
                  {[["pm", "PM", "pm"], ["project_engineer", "PE", "pe"]].map(([f, label, tid]) => (
                    <th key={f} className="whitespace-nowrap px-4 py-2 text-left">
                      <HeaderFilterMenu label={label} testid={`sb-${tid}-filter`} multi separator="|" sortOnLabel hideCount
                                        options={personOpts(f)} value={filterByPerson[f]}
                                        onChange={(v) => setFilterByPerson((w) => ({ ...w, [f]: v || '' }))}
                                        onSort={(dir) => setSort({ key: f, dir })} sortActive={sort.key === f} direction={sort.dir} />
                    </th>))}
                  <SortTh label="Estimator" k="estimator" sort={sort} setSort={setSort} />
                  <SortTh label="Created" k="created" sort={sort} setSort={setSort} className="hidden lg:table-cell" />
                  <SortTh label="Progress" k="progress" sort={sort} setSort={setSort} />
                  <th className="w-10 px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {groups.map((g) => [
                  g.label && (
                    <tr key={`g-${g.label}`} className="bg-muted/30" data-testid={`sb-group-${g.label}`}>
                      <td colSpan={11} className="px-4 py-1.5 text-[11px] font-semibold text-muted-foreground">{g.label} <span className="font-normal">· {g.rows.length}</span></td>
                    </tr>),
                  ...g.rows.map((project) => {
                  const progress = getProjectProgress(project);
                  return (
                    <tr key={project.id} className="transition-colors hover:bg-muted/40" data-testid={`project-row-${project.id}`}>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground" data-testid={`sb-job-number-${project.id}`}>{project.job_number || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleTogglePin(project.id); }}
                            className={`shrink-0 transition-colors ${project.pinned ? 'text-amber-500' : 'text-muted-foreground/30 hover:text-amber-500'}`}
                            title={project.pinned ? 'Unpin' : 'Pin'}
                            data-testid={`pin-toggle-${project.id}`}
                          >
                            <Pin className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleProjectOpen(project)} className="text-left"
                                  data-testid={`select-project-${project.id}-btn`}>
                            <div className="font-medium text-foreground hover:text-primary">{project.name}</div>
                            <div className="text-xs text-muted-foreground">{project.location || 'No address yet'}</div>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <PartyCell value={project.gc} type="gc" testId={`gc-inline-${project.id}`}
                                   onSave={(v) => handleInlineTeamMemberUpdate(project.id, 'gc', v)} />
                      </td>
                      <td className="px-4 py-3">
                        <PartyCell value={project.architect} type="architect" testId={`architect-inline-${project.id}`}
                                   onSave={(v) => handleInlineTeamMemberUpdate(project.id, 'architect', v)} />
                      </td>
                      {[["pm", "pm"], ["project_engineer", "pe"], ["estimator", "est"]].map(([f, kind]) => (
                        <td key={f} className="px-4 py-3">
                          <PersonPick value={project[f]} kind={kind} valueKey="name" asName strict testid={`sb-${kind}-${project.id}`}
                                      onChange={(v) => handleInlineTeamMemberUpdate(project.id, f, v)} />
                        </td>))}
                      <td className="hidden px-4 py-3 text-xs text-muted-foreground lg:table-cell">{formatDate(project.created_at)}</td>
                      <td className="px-4 py-3">
                        {progress.total === 0 ? (
                          <span className="text-sm text-muted-foreground" data-testid={`progress-empty-${project.id}`}>
                            No submittals yet
                          </span>
                        ) : (
                          <div
                            className="min-w-[150px]"
                            data-testid={`progress-cell-${project.id}`}
                            title={progress.blocked.length
                              ? progress.blocked.map((b) => `${b.name} — ${b.reason}`).join('\n')
                              : 'Everything is built'}
                          >
                            <div className="flex h-2 w-[130px] overflow-hidden rounded-full bg-muted">
                              {[
                                { n: progress.submitted, c: 'bg-emerald-600' },
                                { n: progress.built, c: 'bg-green-500' },
                                { n: progress.docs_needed, c: 'bg-amber-500' },
                                { n: progress.ready, c: 'bg-primary' },
                                { n: progress.setup, c: 'bg-slate-300 dark:bg-slate-600' },
                              ].filter((s) => s.n > 0).map((s, si) => (
                                <div key={si} className={`h-full ${s.c}`}
                                     style={{ width: `${(s.n / progress.total) * 100}%` }} />
                              ))}
                            </div>
                            <div className="mt-1 text-xs whitespace-nowrap">
                              <span className="text-foreground">{progress.done} of {progress.total} built</span>
                              {progress.docs_needed > 0 && (
                                <span className="text-amber-600" data-testid={`progress-docs-needed-${project.id}`}>
                                  {' '}· {progress.docs_needed} need docs
                                </span>
                              )}
                              {progress.docs_needed === 0 && progress.setup > 0 && (
                                <span className="text-muted-foreground">
                                  {' '}· {progress.setup} not started
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                      
                      <td className="px-2 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`sb-row-menu-${project.id}`}>
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card">
                            <DropdownMenuItem onClick={() => openEditModal(project)} className="text-muted-foreground cursor-pointer">Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openDeleteDialog(project)} className="text-red-600 cursor-pointer">Archive</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })])}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Results count */}
        {!loading && filteredProjects.length > 0 && (
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle className="text-xl text-foreground">Edit Project</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Update project details.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateProject} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_job_number" className="text-muted-foreground">Job Number *</Label>
                <Input
                  id="edit_job_number"
                  required
                  value={formData.job_number}
                  onChange={(e) => setFormData({...formData, job_number: e.target.value})}
                  className="text-foreground"
                  data-testid="edit-job-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_name" className="text-muted-foreground">Project Name *</Label>
                <Input
                  id="edit_name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="text-foreground"
                  data-testid="edit-project-name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_location" className="text-muted-foreground">Location</Label>
              <Input
                id="edit_location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="text-foreground"
                data-testid="edit-location-input"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_gc" className="text-muted-foreground">General Contractor</Label>
                <Input
                  id="edit_gc"
                  value={formData.gc}
                  onChange={(e) => setFormData({...formData, gc: e.target.value})}
                  className="text-foreground"
                  data-testid="edit-gc-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_architect" className="text-muted-foreground">Architect</Label>
                <Input
                  id="edit_architect"
                  value={formData.architect}
                  onChange={(e) => setFormData({...formData, architect: e.target.value})}
                  className="text-foreground"
                  data-testid="edit-architect-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit_pm" className="text-muted-foreground">Project Manager</Label>
                  {teamMembers.pms.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        if (formData.pm) {
                          openDeleteTeamMemberDialog('pm', formData.pm);
                        }
                      }}
                      disabled={!formData.pm}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
                {!showNewPmInput ? (
                  <Select
                    value={formData.pm}
                    onValueChange={(value) => {
                      if (value === '__new__') {
                        setShowNewPmInput(true);
                        setFormData({...formData, pm: ''});
                      } else {
                        setFormData({...formData, pm: value});
                      }
                    }}
                  >
                    <SelectTrigger className="text-foreground" data-testid="edit-pm-select">
                      <SelectValue placeholder="Select PM (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                      {teamMembers.pms.map((pm) => (
                        <SelectItem key={pm} value={pm}>{pm}</SelectItem>
                      ))}
                      <SelectItem value="__new__">+ Add new PM</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter PM name"
                      value={formData.pm}
                      onChange={(e) => setFormData({...formData, pm: e.target.value})}
                      className="text-foreground flex-1"
                      data-testid="edit-pm-new-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowNewPmInput(false);
                        setFormData({...formData, pm: ''});
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit_estimator" className="text-muted-foreground">Estimator</Label>
                  {teamMembers.estimators.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        if (formData.estimator) {
                          openDeleteTeamMemberDialog('estimator', formData.estimator);
                        }
                      }}
                      disabled={!formData.estimator}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
                {!showNewEstimatorInput ? (
                  <Select
                    value={formData.estimator}
                    onValueChange={(value) => {
                      if (value === '__new__') {
                        setShowNewEstimatorInput(true);
                        setFormData({...formData, estimator: ''});
                      } else {
                        setFormData({...formData, estimator: value});
                      }
                    }}
                  >
                    <SelectTrigger className="text-foreground" data-testid="edit-estimator-select">
                      <SelectValue placeholder="Select Estimator (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                      {teamMembers.estimators.map((est) => (
                        <SelectItem key={est} value={est}>{est}</SelectItem>
                      ))}
                      <SelectItem value="__new__">+ Add new Estimator</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter Estimator name"
                      value={formData.estimator}
                      onChange={(e) => setFormData({...formData, estimator: e.target.value})}
                      className="text-foreground flex-1"
                      data-testid="edit-estimator-new-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowNewEstimatorInput(false);
                        setFormData({...formData, estimator: ''});
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter className="gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditModalOpen(false);
                  resetForm();
                  setCurrentProject(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-update-project-btn">
                Update Project
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-foreground">
              <Trash2 className="h-5 w-5 text-red-500" />
              Archive Project?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This action will archive the selected project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-base text-muted-foreground">
              Are you sure you want to archive <span className="font-semibold text-foreground">{projectToDelete?.name || 'this project'}</span>?
            </p>
            {projectToDelete && (
              <div className="bg-muted p-3 rounded-lg border border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="h-3 w-3" />
                  <span>Job #{projectToDelete.job_number}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteProject}
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Member Confirmation Dialog */}
      <Dialog open={deleteTeamMemberOpen} onOpenChange={setDeleteTeamMemberOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-foreground">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete {teamMemberToDelete.type === 'pm' ? 'Project Manager' : teamMemberToDelete.type === 'project_engineer' ? 'Project Engineer' : 'Estimator'}?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This action will remove this team member from all projects.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-base text-muted-foreground">
              Are you sure you want to delete <span className="font-semibold text-foreground">"{teamMemberToDelete.name}"</span>?
            </p>
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> This will remove this {teamMemberToDelete.type === 'pm' ? 'PM' : teamMemberToDelete.type === 'project_engineer' ? 'Project Engineer' : 'Estimator'} from all projects where they are assigned. This action cannot be undone.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteTeamMemberOpen(false);
                setTeamMemberToDelete({ type: '', name: '' });
              }}
              data-testid="cancel-delete-team-member-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTeamMember}
              data-testid="confirm-delete-team-member-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Simple Add PM/Estimator/Project Engineer Name Dialog */}
      <Dialog open={addNameDialog.open} onOpenChange={(open) => {
        if (!open) {
          setAddNameDialog({ open: false, type: '', projectId: '' });
          setNewNameInput('');
        }
      }}>
        <DialogContent className="bg-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-foreground">
              <UserPlus className="h-5 w-5 text-primary" />
              Add New {addNameDialog.type === 'pm' ? 'Project Manager' : addNameDialog.type === 'project_engineer' ? 'Project Engineer' : 'Estimator'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Enter the name of the {addNameDialog.type === 'pm' ? 'Project Manager' : addNameDialog.type === 'project_engineer' ? 'Project Engineer' : 'Estimator'} to add.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-name" className="text-muted-foreground">
                {addNameDialog.type === 'pm' ? 'PM Name' : addNameDialog.type === 'project_engineer' ? 'PE Name' : 'Estimator Name'}
              </Label>
              <Input
                id="new-name"
                placeholder={`Enter ${addNameDialog.type === 'pm' ? 'PM' : addNameDialog.type === 'project_engineer' ? 'Project Engineer' : 'Estimator'} name`}
                value={newNameInput}
                onChange={(e) => setNewNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddNewName();
                  }
                }}
                className="text-foreground"
                data-testid="add-name-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setAddNameDialog({ open: false, type: '', projectId: '' });
                setNewNameInput('');
              }}
              data-testid="cancel-add-name-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNewName}
              disabled={!newNameInput.trim()}
              data-testid="confirm-add-name-btn"
              className="btn-primary"
            >
              Add {addNameDialog.type === 'pm' ? 'PM' : addNameDialog.type === 'project_engineer' ? 'PE' : 'Estimator'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
